import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Loader2 } from 'lucide-react';
import { optimizationService } from '../../services/optimization/OptimizationService';
import { OptimizationPrompt } from '../../services/optimization/OptimizationService';

interface AddPromptDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onPromptAdded: () => void;
}

type MarketplaceType = 'Amazon' | 'Google Shopping' | 'Shopify';

const STANDARD_PROMPTS: Record<MarketplaceType, { title: string, id: string }> = {
  'Amazon': { title: 'Standard Amazon Optimization', id: 'default-amazon-prompt' },
  'Google Shopping': { title: 'Standard Google Shopping Optimization', id: 'default-google-shopping-prompt' },
  'Shopify': { title: 'Standard Shopify Optimization', id: 'default-shopify-prompt' }
} as const;

const AddPromptDialog = ({
  isOpen,
  onOpenChange,
  onPromptAdded
}: AddPromptDialogProps): JSX.Element => {
  const [marketplace, setMarketplace] = useState<MarketplaceType | ''>('');
  const [title, setTitle] = useState('');
  const [promptText, setPromptText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [standardPrompts, setStandardPrompts] = useState<Partial<Record<MarketplaceType, string>>>({});
  const [hasLoadedStandardPrompt, setHasLoadedStandardPrompt] = useState(false);

  // Load standard prompts on mount
  useEffect(() => {
    const loadStandardPrompts = async () => {
      try {
        const standardPromptsMap: Partial<Record<MarketplaceType, string>> = {};
        
        // Load prompts for each marketplace
        for (const marketplace of Object.keys(STANDARD_PROMPTS) as MarketplaceType[]) {
          const prompts = await optimizationService.getPrompts(marketplace);
          const standardPrompt = prompts.find(p => p.isDefault);
          if (standardPrompt) {
            standardPromptsMap[marketplace] = standardPrompt.content;
          }
        }

        console.log('Loaded standard prompts:', standardPromptsMap);
        setStandardPrompts(standardPromptsMap);
      } catch (error) {
        console.error('Error loading standard prompts:', error);
      }
    };

    loadStandardPrompts();
  }, []);

  // Handle marketplace change
  useEffect(() => {
    if (hasLoadedStandardPrompt && marketplace && standardPrompts[marketplace]) {
      setPromptText(standardPrompts[marketplace] || '');
    }
  }, [marketplace, standardPrompts, hasLoadedStandardPrompt]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!marketplace || !title || !promptText) return;

    setIsLoading(true);
    setError(null);
    try {
      const newPrompt: OptimizationPrompt = {
        id: title.toLowerCase().replace(/\s+/g, '-'),
        marketplace,
        title,
        content: promptText,
        isDefault: false,
        lastUsed: new Date()
      };

      await optimizationService.savePrompt(newPrompt);
      
      onPromptAdded();
      onOpenChange(false);
      setMarketplace('');
      setTitle('');
      setPromptText('');
      setHasLoadedStandardPrompt(false);
    } catch (error: any) {
      console.error('Error adding prompt:', error);
      setError(error.message || 'Failed to save prompt. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditStandardPrompt = () => {
    if (marketplace && standardPrompts[marketplace]) {
      setPromptText(standardPrompts[marketplace] || '');
      setHasLoadedStandardPrompt(true);
    }
  };

  const handleMarketplaceChange = (value: string) => {
    // Special handling for Google Shopping
    const normalizedMarketplace = value.toLowerCase() === 'google shopping' 
      ? 'Google Shopping'
      : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

    if (Object.keys(STANDARD_PROMPTS).includes(normalizedMarketplace as MarketplaceType)) {
      setMarketplace(normalizedMarketplace as MarketplaceType);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        setMarketplace('');
        setTitle('');
        setPromptText('');
        setHasLoadedStandardPrompt(false);
      }
      onOpenChange(open);
    }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Prompt</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marketplace
            </label>
            <Select
              value={marketplace}
              onValueChange={handleMarketplaceChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select marketplace" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Amazon">Amazon</SelectItem>
                <SelectItem value="Google Shopping">Google Shopping</SelectItem>
                <SelectItem value="Shopify">Shopify</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter prompt title"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Prompt
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleEditStandardPrompt}
                disabled={!marketplace || !standardPrompts[marketplace]}
              >
                Edit Standard Prompt
              </Button>
            </div>
            <Textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Enter prompt text"
              className="min-h-[200px]"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !marketplace || !title || !promptText}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Prompt'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPromptDialog;